import { useEffect } from 'react';

interface PageRefreshWarningProps {
  /** Whether to show the warning (typically when form has unsaved changes) */
  enabled: boolean;
  /** Custom warning message */
  message?: string;
}

/**
 * Component that warns users before leaving the page with unsaved changes
 * Prevents accidental data loss from closing tab, refreshing, or navigating away
 *
 * @example
 * const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
 *
 * return (
 *   <>
 *     <PageRefreshWarning enabled={hasUnsavedChanges} />
 *     <Form onChange={() => setHasUnsavedChanges(true)}>
 *       ...
 *     </Form>
 *   </>
 * );
 */
export const PageRefreshWarning: React.FC<PageRefreshWarningProps> = ({
  enabled,
  message = 'You have unsaved changes. Are you sure you want to leave?',
}) => {
  useEffect(() => {
    if (!enabled) return;

    // Handler for browser refresh/close
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers require returnValue to be set
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, message]);

  // This component doesn't render anything
  return null;
};

export default PageRefreshWarning;
